<template>
  <form class="crypto-withdraw-page" :class="{ 'crypto-withdraw-page--dark': $vuetify.theme.dark }" @submit.prevent="handleSubmit">
    <v-container fluid class="main-container responsive-height crypto-withdraw-container">
      <header class="crypto-withdraw-heading"><span><v-icon>mdi-bitcoin</v-icon></span><div><h1>Crypto Withdraw</h1><p>Withdraw funds securely to your saved cryptocurrency address.</p></div></header>
      <v-row>
        <v-col xl="6" lg="7" md="7" sm="7" cols="12">
          <v-card class="deposit-form crypto-withdraw-form-card">
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Transfer Method</legend>
                  <v-text-field label="Crypto Transfer" disabled placeholder="Crypto Transfer" single-line solo
                    hide-details readonly class="custom-select" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Withdraw From</legend>
                  <v-select v-model="selectedAccount" :items="formattedAccounts" item-text="display"
                    item-value="trading_account_no" solo append-icon="mdi-chevron-down" label="Select One"
                    class="custom-select" hide-details @change="handleAccountChange"
                    :rules="[(v) => !!v || 'Account is required']" :error="errors.account"
                    :error-messages="errors.account ? ['Account is required'] : []" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12"
                v-if="selectedAccountDetails && Number(selectedAccountDetails.trader_type) === 2">
                <fieldset>
                  <legend>Investor Available Withdraw Amount</legend>
                  <v-text-field
                    :value="`${selectedAccountDetails.currency_symbol || '$'} ${selectedAccountDetails.availableWithdraw} ( MAM Profit Amount is ${selectedAccountDetails.profitAmount}$ )`"
                    readonly solo hide-details class="custom-select" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>To Your Crypto Address</legend>
                  <v-select v-model="wallet_address" :items="addresses" item-text="renamedAddress" item-value="address"
                    solo append-icon="mdi-chevron-down" label="Select One" class="custom-select" hide-details
                    @change="handleWalletAddress" :rules="[(v) => !!v || 'Wallet address is required']"
                    :error="errors.wallet_address"
                    :error-messages="errors.wallet_address ? ['Wallet address is required'] : []" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" :md="currency.name !== 'USD' ? 6 : 12">
                <fieldset>
                  <legend>
                    Amount in (USD)
                    <small v-if="minTransactionUSD > 0" class="ms-2 text-muted">
                      (Min: ${{ minTransactionUSD }})
                    </small>
                  </legend>

                  <v-text-field v-model="withdrawAmount"
                    :label="minTransactionUSD > 0 ? `Minimum withdraw amount ${minTransactionUSD}$` : '$'" type="number"
                    single-line solo hide-details class="custom-select"
                    :min="minTransactionUSD > 0 ? minTransactionUSD : 1" :rules="amountRules" :error="errors.amount"
                    :error-messages="amountErrorMessages" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="6" v-if="currency.name !== 'USD'">
                <fieldset>
                  <legend>
                    Amount in <span :style="{ color: currency.color }">({{ currency.name }})</span>
                  </legend>
                  <v-text-field readonly v-model="currencyAmount" :label="currency.symbol" type="number" single-line
                    solo hide-details class="custom-select text-muted" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <v-btn tile elevation="0" type="submit" :loading="loading" :disabled="!isFormValid || loading"
                  class="ma-0 text-capitalize btn-account d-block crypto-withdraw-button">
                  Withdraw
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col xl="4" lg="4" md="5" sm="5" cols="12">
          <v-card v-if="selectedAccountDetails" class="elevation-0 deposit-form-card crypto-withdraw-summary">
            <v-card-actions>
              <div class="trade-status">
                <div>
                  <v-icon>mdi-signal</v-icon>
                </div>
                <div class="trade-number">
                  <span>MT5 - {{ selectedAccountDetails.account_type_display }}</span>
                  <span>{{ selectedAccountDetails.trading_account_no }}</span>
                </div>
              </div>
            </v-card-actions>

            <v-card-actions>
              <div class="value-trades">
                <span>Balance</span>
                <span>{{ selectedAccountDetails.Balance }}</span>
              </div>
              <v-spacer></v-spacer>
              <div class="value-trades">
                <span>Currency</span>
                <span>{{ selectedAccountDetails.currency_name }}</span>
              </div>
            </v-card-actions>

            <v-card-actions>
              <v-sheet :color="$vuetify.theme.dark ? 'transparent' : 'transparent'">
                <div>
                  <apexChart width="100%" height="95px" type="line" :options="options" :series="series" />
                </div>
              </v-sheet>
            </v-card-actions>

            <v-card-actions>
              <div class="currency-leverage">
                <span>Free Funds</span>
                <span>{{ selectedAccountDetails.MarginFree }}</span>
              </div>
              <v-spacer></v-spacer>
              <div class="currency-leverage">
                <span>Leverage</span>
                <span>{{ selectedAccountDetails.leverage }}:1</span>
              </div>
            </v-card-actions>

            <v-card-actions>
              <div class="balance-equity">
                <span>Free Margin</span>
                <span>{{ selectedAccountDetails.MarginFree }}</span>
              </div>
              <v-spacer></v-spacer>
              <div class="balance-equity">
                <span>Acc. Type</span>
                <span>{{ selectedAccountDetails.account_type_display }}</span>
              </div>
            </v-card-actions>

            <v-card-actions v-if="Number(selectedAccountDetails.trader_type) === 2" class="mt-4">
              <v-alert type="info" dense text class="w-100">
                This is an <strong>Investor Account</strong>. You can withdraw from your <strong>capital only</strong>.
                Your profit amount of <strong>{{ selectedAccountDetails.profitAmount }}</strong> will be processed by
                the
                MAM system.
              </v-alert>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { WITHDRAWMETHODS__ACTIONS } from "@/store/action-types";
import toast from "@/services/Notification";

export default {
  data() {
    return {
      payment_id: null,

      // dynamic min withdraw amount in USD (payment_settings.min_transaction)
      minTransactionUSD: 0,

      selectedAccount: null,
      withdrawAmount: "",
      currencyAmount: "",
      currency: {
        rate: 1,
        name: "USD",
        symbol: "$",
        color: "#ffc400",
      },

      wallet_address: "",
      network: "",
      loading: false,
      errors: {
        account: false,
        amount: false,
        wallet_address: false,
      },

      options: {
        chart: { type: "line", toolbar: { show: false } },
        xaxis: { labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } },
        yaxis: { labels: { show: false } },
        grid: { show: false },
        stroke: { width: 2 },
        dataLabels: { enabled: false },
        colors: ["#ffc400"],
      },
      series: [{ name: "Series 1", data: [45, 52, 38, 45, 19, 23, 2] }],
    };
  },

  computed: {
    // methods added (so we can read min_transaction by payment_id)
    ...mapState("withdraw", ["accounts", "addresses", "methods"]),

    formattedAccounts() {
      return this.accounts.map((account) => ({
        display: `${account.trading_account_no} (${account.account_type_display}) ${account.Balance} ${account.currency_name}`,
        trading_account_no: account.trading_account_no,
        ...account,
      }));
    },

    selectedAccountDetails() {
      return this.accounts.find((acc) => acc.trading_account_no === this.selectedAccount);
    },

    availableFunds() {
      if (!this.selectedAccountDetails) return 0;
      // prefer availableMarginFree if exists, otherwise MarginFree
      return Number(this.selectedAccountDetails.availableMarginFree ?? this.selectedAccountDetails.MarginFree ?? 0);
    },

    amountRules() {
      return [
        (v) => !!v || "Amount is required",
        (v) => parseFloat(v) > 0 || "Amount must be greater than 0",
        (v) =>
          !this.minTransactionUSD ||
          parseFloat(v) >= this.minTransactionUSD ||
          `Minimum withdrawal is $${this.minTransactionUSD}`,
        (v) =>
          !this.selectedAccountDetails ||
          parseFloat(this.currencyAmount || 0) <= this.availableFunds ||
          "Amount exceeds available funds",
      ];
    },

    amountErrorMessages() {
      if (!this.errors.amount) return [];
      const amt = parseFloat(this.withdrawAmount);

      if (!this.withdrawAmount || !Number.isFinite(amt) || amt <= 0) return ["Amount is required"];
      if (this.minTransactionUSD && amt < this.minTransactionUSD) return [`Minimum withdrawal is $${this.minTransactionUSD}`];
      if (this.selectedAccountDetails && parseFloat(this.currencyAmount || 0) > this.availableFunds) return ["Amount exceeds available funds"];

      return ["Invalid amount"];
    },

    isFormValid() {
      const amt = parseFloat(this.withdrawAmount);
      return (
        this.selectedAccount &&
        this.wallet_address &&
        this.withdrawAmount &&
        Number.isFinite(amt) &&
        amt > 0 &&
        (this.minTransactionUSD ? amt >= this.minTransactionUSD : true) &&
        (!this.selectedAccountDetails || parseFloat(this.currencyAmount || 0) <= this.availableFunds)
      );
    },
  },

  watch: {
    withdrawAmount(newAmount) {
      const amt = parseFloat(newAmount);
      if (!Number.isFinite(amt)) {
        this.currencyAmount = "";
        return;
      }

      if (this.currency && this.currency.rate) {
        this.currencyAmount = (amt * this.currency.rate).toFixed(2);
      } else {
        this.currencyAmount = String(amt);
      }
    },

    methods: {
      handler() {
        this.pickMinTransactionFromMethods();
      },
      immediate: true,
    },
    payment_id() {
      this.pickMinTransactionFromMethods();
    },
  },

  methods: {
    ...mapActions("withdraw", [
      WITHDRAWMETHODS__ACTIONS.WITHDRAWACCOUNTS,
      WITHDRAWMETHODS__ACTIONS.WITHDRAWMETHODS, // fetch methods so we can read min_transaction
      WITHDRAWMETHODS__ACTIONS.CREATECRYPTOWITHDRAW,
      WITHDRAWMETHODS__ACTIONS.USERCRYPTOADDRESS,
    ]),

    // find payment_settings.min_transaction using URL payment_id
    pickMinTransactionFromMethods() {
      try {
        if (!this.payment_id || !Array.isArray(this.methods)) return;

        const method =
          this.methods.find(
            (m) =>
              String(m.id) === String(this.payment_id) ||
              String(m.payment_id) === String(this.payment_id) ||
              String(m.payment_settings_id) === String(this.payment_id)
          ) || null;

        const min =
          method?.payment_settings?.min_transaction ??
          method?.min_transaction ??
          method?.minTransaction ??
          0;

        this.minTransactionUSD = Number(min) || 0;
      } catch (e) {
        this.minTransactionUSD = 0;
      }
    },

    handleAccountChange() {
      this.withdrawAmount = "";
      this.errors.account = false;
      this.errors.amount = false;
      this.currencyAmount = "";

      const account_details = this.accounts.find((acc) => acc.trading_account_no === this.selectedAccount);
      if (account_details) {
        this.currency.rate = account_details.currency_rate;
        this.currency.name = account_details.currency_name;
        this.currency.symbol = account_details.currency_symbol;
        this.currency.color = account_details.symbol_color;
      }
    },

    handleWalletAddress() {
      this.network = this.addresses
        ? this.addresses?.find((item) => item?.address === this.wallet_address)?.network
        : "";
      this.errors.wallet_address = false;
    },

    validateForm() {
      const amt = parseFloat(this.withdrawAmount);

      this.errors.account = !this.selectedAccount;
      this.errors.wallet_address = !this.wallet_address;

      this.errors.amount =
        !this.withdrawAmount ||
        !Number.isFinite(amt) ||
        amt <= 0 ||
        (this.minTransactionUSD ? amt < this.minTransactionUSD : false) ||
        (this.selectedAccountDetails ? parseFloat(this.currencyAmount || 0) > this.availableFunds : false);

      return !this.errors.account && !this.errors.amount && !this.errors.wallet_address;
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        const amt = parseFloat(this.withdrawAmount);

        if (this.minTransactionUSD && Number.isFinite(amt) && amt < this.minTransactionUSD) {
          toast.error(`Minimum withdrawal is $${this.minTransactionUSD}`);
        } else if (!this.wallet_address) {
          toast.error("Wallet address is required");
        } else if (this.selectedAccountDetails && parseFloat(this.currencyAmount || 0) > this.availableFunds) {
          toast.error("Amount exceeds available funds");
        } else {
          toast.error("Please fill all required fields correctly");
        }
        return;
      }

      this.loading = true;
      try {
        const withdrawalData = {
          trading_account_no: this.selectedAccount,
          amount: this.withdrawAmount,
          wallet_address: this.wallet_address,
          network: this.network,
          payment_id: this.payment_id,
        };

        const res = await this[WITHDRAWMETHODS__ACTIONS.CREATECRYPTOWITHDRAW](withdrawalData);
        if(res === true){
          this.resetForm();
        }
        
      } catch (error) {
        toast.error(error.message || "Failed to process withdrawal");
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.selectedAccount = null;
      this.withdrawAmount = "";
      this.wallet_address = "";
      this.network = "";
      this.currencyAmount = "";
      this.errors = {
        account: false,
        amount: false,
        wallet_address: false,
      };
    },
  },

  async mounted() {
    this.payment_id = this.$route.query.payment_id;

    await this[WITHDRAWMETHODS__ACTIONS.WITHDRAWACCOUNTS]();
    await this[WITHDRAWMETHODS__ACTIONS.USERCRYPTOADDRESS]();

    //  load methods to get min_transaction by payment_id
    await this[WITHDRAWMETHODS__ACTIONS.WITHDRAWMETHODS]();
    this.pickMinTransactionFromMethods();
  },
};
</script>

<style lang="scss" scoped>
.crypto-withdraw-page {
  --cw-page: #f7f8fa; --cw-panel: #fff; --cw-field: #fff; --cw-border: #dfe2e6;
  --cw-text: #171a1f; --cw-muted: #626973; --cw-gold: #d99a00;
  box-sizing: border-box; height: calc(100vh - 64px); min-height: 100%; overflow-x: hidden; overflow-y: auto;
  color: var(--cw-text); background: var(--cw-page); overscroll-behavior-y: contain; -webkit-overflow-scrolling: touch;
}
.crypto-withdraw-page--dark {
  --cw-page: #030b13; --cw-panel: #07111b; --cw-field: #08131e; --cw-border: #293026;
  --cw-text: #f4f5f7; --cw-muted: #aeb6c0; --cw-gold: #ffc400;
  background: radial-gradient(circle at 70% 14%, rgba(255, 196, 0, .05), transparent 34%), var(--cw-page);
}
.crypto-withdraw-container { box-sizing: border-box; max-width: none !important; min-height: 100%; padding: 16px 18px 24px !important; }
.crypto-withdraw-heading { min-height: 64px; display: flex; align-items: center; gap: 13px; margin-bottom: 8px; }
.crypto-withdraw-heading > span { width: 42px; height: 42px; display: inline-flex; align-items: center; justify-content: center; color: var(--cw-gold); border: 1px solid rgba(217, 154, 0, .42); border-radius: 9px; background: rgba(217, 154, 0, .09); }
.crypto-withdraw-heading .v-icon { color: inherit; }
.crypto-withdraw-heading h1 { margin: 0 0 3px; color: var(--cw-text); font-size: 23px; line-height: 1.2; }
.crypto-withdraw-heading p { margin: 0; color: var(--cw-muted); font-size: 13px; }
.crypto-withdraw-form-card, .crypto-withdraw-summary { color: var(--cw-text) !important; border: 1px solid var(--cw-border); border-radius: 10px !important; background: var(--cw-panel) !important; box-shadow: none !important; }
.crypto-withdraw-form-card { padding: 12px 14px; }
/* Preserve original field geometry; update theme colors only. */
.crypto-withdraw-page .deposit-form fieldset { border-color: var(--cw-border) !important; background: var(--cw-field) !important; }
.crypto-withdraw-page .deposit-form fieldset legend { color: var(--cw-text) !important; background: var(--cw-panel) !important; }
.crypto-withdraw-page::v-deep .deposit-form fieldset .custom-select .v-input__slot { border-color: transparent !important; background: var(--cw-field) !important; }
.crypto-withdraw-page::v-deep .deposit-form fieldset .custom-select input,
.crypto-withdraw-page::v-deep .deposit-form fieldset .custom-select .v-select__selection { color: var(--cw-text) !important; }
.crypto-withdraw-page::v-deep .deposit-form fieldset .custom-select .v-label,
.crypto-withdraw-page::v-deep .deposit-form fieldset .custom-select input::placeholder { color: var(--cw-muted) !important; opacity: .78; }
.crypto-withdraw-page::v-deep .deposit-form fieldset .custom-select .v-icon { color: var(--cw-gold) !important; }
.crypto-withdraw-page legend small { color: var(--cw-gold) !important; }
.crypto-withdraw-button { width: 100%; height: 44px !important; color: #17130a !important; border-radius: 6px !important; background: linear-gradient(135deg, #ffd43b, #ffc400) !important; font-weight: 700; }
.crypto-withdraw-summary { overflow: hidden; }
.crypto-withdraw-summary > .v-card__actions { min-height: 58px; padding: 12px 16px; border-bottom: 1px solid var(--cw-border); }
.crypto-withdraw-summary > .v-card__actions:last-child { border-bottom: 0; }
.trade-status { display: flex; align-items: center; gap: 11px; }
.trade-status > div:first-child { width: 39px; height: 39px; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(217, 154, 0, .1); }
.trade-status .v-icon { color: var(--cw-gold) !important; }
.trade-number, .value-trades, .currency-leverage, .balance-equity { display: flex; flex-direction: column; gap: 3px; }
.trade-number span:first-child, .value-trades span:last-child, .currency-leverage span:last-child, .balance-equity span:last-child { color: var(--cw-text); font-weight: 600; }
.trade-number span:last-child, .value-trades span:first-child, .currency-leverage span:first-child, .balance-equity span:first-child { color: var(--cw-muted); font-size: 11px; }
.crypto-withdraw-summary::v-deep .v-alert { color: var(--cw-muted) !important; border-color: rgba(217, 154, 0, .3) !important; background: rgba(217, 154, 0, .06) !important; }
.crypto-withdraw-summary::v-deep .v-alert .v-icon { color: var(--cw-gold) !important; }
@media (max-width: 760px) {
  .crypto-withdraw-page { height: calc(100vh - 56px); height: calc(100dvh - 56px); }
  .crypto-withdraw-container { padding: 12px 10px 20px !important; }
  .crypto-withdraw-container > .row { margin-right: -6px; margin-left: -6px; }
  .crypto-withdraw-container > .row > [class*="col-"] { width: 100%; max-width: 100%; flex: 0 0 100%; padding: 6px; }
  .crypto-withdraw-heading p { display: none; }
  .crypto-withdraw-heading h1 { font-size: 21px; }
  .crypto-withdraw-form-card { padding: 7px; }
  .crypto-withdraw-form-card > .row { margin-right: -6px; margin-left: -6px; }
  .crypto-withdraw-form-card > .row > [class*="col-"] { padding: 6px; }
  .crypto-withdraw-button { width: 100%; }
  .crypto-withdraw-summary { margin-top: 6px !important; }
}
@media (max-width: 380px) {
  .crypto-withdraw-heading > span { width: 38px; height: 38px; }
  .crypto-withdraw-heading h1 { font-size: 19px; }
}
</style>
